if (!customElements.get("m-recently-viewed")) {
  class MRecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.selectors = {
        slideControls: ".m-slider-controls",
        slideContainer: ".m-mixed-layout__wrapper",
      };
      this.domNodes = queryDomNodes(this.selectors, this);
      this.enableSlider = this.dataset.enableSlider === "true";
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.items = this.dataset.productsPerRow;
      this.mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      this.productsToshow = parseInt(this.dataset.productsToShow, 10) || 8;
      this.storageKey = "minimog-recently-viewed";
      this.handleStorageKey = "m-recent-viewed-products";
      this.excludedProductIds = new Set();
      this.excludedHandles = new Set();
      this.requestedItems = [];
      this.handleScreenSizeChange = this.initByScreenSize.bind(this);
      this.screenSizeListenersBound = false;
      this.init();
    }

    disconnectedCallback() {
      if (this.renderingObserver) {
        this.renderingObserver.disconnect();
        this.renderingObserver = null;
      }
      if (this.screenSizeListenersBound) {
        document.removeEventListener("matchMobile", this.handleScreenSizeChange);
        document.removeEventListener("unmatchMobile", this.handleScreenSizeChange);
        this.screenSizeListenersBound = false;
      }
      if (this.swiper && !this.swiper.destroyed) this.swiper.destroy(false, true);
    }

    init() {
      this.deferProductRendering();
    }

    async beginProductRendering() {
      const complianceReady = await this.loadComplianceExclusions();
      if (!complianceReady) {
        this.classList.add("m:hidden");
        return;
      }
      this.loadProducts();
    }

    deferProductRendering() {
      if (!("IntersectionObserver" in window)) {
        this.beginProductRendering();
        return;
      }

      this.renderingObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.renderingObserver = null;
          this.beginProductRendering();
        },
        { rootMargin: "0px 0px 500px 0px" }
      );
      this.renderingObserver.observe(this);
    }

    async loadProducts() {
      const query = this.getQueryString();
      if (!query) return;

      try {
        const response = await fetch(this.dataset.url + encodeURIComponent(query), {
          credentials: "same-origin",
        });
        if (!response.ok) throw new Error(`Recently Viewed search failed: ${response.status}`);

        const text = await response.text();
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("m-recently-viewed");
        if (!recommendations) {
          this.removeUnavailableRequestedItems(new Set());
          return;
        }

        const productCards = recommendations.querySelectorAll(".m-product-card[data-product-id]");
        const availableProductIds = new Set(
          Array.from(productCards, (card) => Number(card.dataset.productId)).filter(Number.isSafeInteger)
        );
        const availableProductHandles = new Set(
          Array.from(productCards, (card) => {
            const href = card.querySelector('a[href*="/products/"]')?.getAttribute("href") || "";
            const match = href.match(/\/products\/([^/?#]+)/);
            return match ? decodeURIComponent(match[1]) : "";
          }).filter(Boolean)
        );
        this.removeUnavailableRequestedItems(availableProductIds);
        this.removeUnavailableStoredHandles(availableProductHandles);
        this.products = productCards.length;

        if (this.products > 0) this.classList.remove("m:hidden");
        if (!recommendations.innerHTML.trim().length) return;

        this.innerHTML = recommendations.innerHTML;
        this.initByScreenSize();
        if (!this.screenSizeListenersBound) {
          document.addEventListener("matchMobile", this.handleScreenSizeChange);
          document.addEventListener("unmatchMobile", this.handleScreenSizeChange);
          this.screenSizeListenersBound = true;
        }
        MinimogTheme.CompareProduct?.setCompareButtonsState();
        MinimogTheme.Wishlist?.setWishlistButtonsState();
      } catch (error) {
        console.error(error);
      }
    }

    async loadComplianceExclusions() {
      const url = this.dataset.complianceExclusionsUrl;
      const expectedVersion = this.dataset.complianceCandidateVersion;
      const expectedHash = this.dataset.complianceCandidateHash;
      const expectedCount = Number(this.dataset.complianceCandidateCount);
      if (!url || !expectedVersion || !expectedHash || !Number.isSafeInteger(expectedCount)) return false;

      try {
        const response = await fetch(url, {
          cache: "force-cache",
          credentials: "same-origin",
        });
        if (!response.ok) throw new Error(`Compliance lookup failed: ${response.status}`);

        const data = await response.json();
        const productIds = Array.isArray(data.candidate_product_ids)
          ? data.candidate_product_ids.map(Number).filter(Number.isSafeInteger)
          : [];
        const handles = Array.isArray(data.candidate_handles)
          ? data.candidate_handles.filter((handle) => typeof handle === "string" && handle.length > 0)
          : [];

        if (
          data.version !== expectedVersion ||
          data.candidate_hash !== expectedHash ||
          data.candidate_count !== expectedCount ||
          productIds.length !== expectedCount ||
          handles.length !== expectedCount ||
          new Set(productIds).size !== expectedCount ||
          new Set(handles).size !== expectedCount
        ) {
          throw new Error("Compliance lookup version mismatch");
        }

        this.excludedProductIds = new Set(productIds);
        this.excludedHandles = new Set(handles);
        this.cleanupCandidateCaches();
        return true;
      } catch (error) {
        console.warn("Recently Viewed was disabled because its compliance lookup was unavailable.", error);
        return false;
      }
    }

    cleanupCandidateCaches() {
      const storedItems = this.readStoredProductIds();
      const eligibleItems = storedItems.filter((item) => !this.excludedProductIds.has(item));
      if (eligibleItems.length !== storedItems.length) this.writeStoredProductIds(eligibleItems);

      const storedHandles = this.readStoredHandles();
      const eligibleHandles = storedHandles.filter((handle) => !this.excludedHandles.has(handle));
      if (eligibleHandles.length !== storedHandles.length) this.writeStoredHandles(eligibleHandles);
    }

    readStoredProductIds() {
      try {
        const value = JSON.parse(window.localStorage.getItem(this.storageKey) || "[]");
        if (!Array.isArray(value)) return [];
        return Array.from(
          new Set(value.map(Number).filter((item) => Number.isSafeInteger(item) && item > 0))
        ).slice(0, 20);
      } catch (error) {
        this.writeStoredProductIds([]);
        return [];
      }
    }

    writeStoredProductIds(items) {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(items.slice(0, 20)));
      } catch (error) {
        console.warn("Recently Viewed storage cleanup could not be saved.", error);
      }
    }

    readStoredHandles() {
      try {
        const prefix = `${this.handleStorageKey}=`;
        const entry = document.cookie
          .split(";")
          .map((item) => item.trim())
          .find((item) => item.startsWith(prefix));
        if (!entry) return [];
        const value = JSON.parse(decodeURIComponent(entry.slice(prefix.length)));
        return Array.isArray(value)
          ? Array.from(new Set(value.filter((handle) => typeof handle === "string"))).slice(0, 20)
          : [];
      } catch (error) {
        return [];
      }
    }

    writeStoredHandles(handles) {
      document.cookie = `${this.handleStorageKey}=${encodeURIComponent(JSON.stringify(handles.slice(0, 20)))};path=/;SameSite=Lax`;
    }

    removeUnavailableRequestedItems(availableProductIds) {
      if (!this.requestedItems.length) return;
      const unavailableProductIds = new Set(
        this.requestedItems.filter((item) => !availableProductIds.has(item))
      );
      if (!unavailableProductIds.size) return;
      const remainingItems = this.readStoredProductIds().filter(
        (item) => !unavailableProductIds.has(item)
      );
      this.writeStoredProductIds(remainingItems);
    }

    removeUnavailableStoredHandles(availableProductHandles) {
      const currentProductHandle = this.dataset.productHandle;
      if (currentProductHandle) availableProductHandles.add(currentProductHandle);

      const storedHandles = this.readStoredHandles();
      const remainingHandles = storedHandles.filter((handle) => availableProductHandles.has(handle));
      if (remainingHandles.length !== storedHandles.length) this.writeStoredHandles(remainingHandles);
    }

    getQueryString() {
      const currentProductId = Number(this.dataset.productId);
      const items = this.readStoredProductIds().filter((item) => {
        if (this.excludedProductIds.has(item)) return false;
        return !Number.isSafeInteger(currentProductId) || item !== currentProductId;
      });
      this.requestedItems = items.slice(0, this.productsToshow);
      return this.requestedItems.map((item) => `id:${item}`).join(" OR ");
    }

    initByScreenSize() {
      if (!this.enableSlider) return;
      const { slideContainer, slideControls } = queryDomNodes(this.selectors, this);

      if (MinimogTheme.config.mqlMobile && this.mobileDisableSlider) {
        if (slideControls) slideControls.classList.add("m:hidden");
        if (slideContainer) slideContainer.classList.remove("swiper-container");
        if (this.swiper) this.swiper.destroy(false, true);
      } else {
        if (slideControls) slideControls.classList.remove("m:hidden");
        this.initSlider();
      }

      if (this.products <= parseInt(this.items, 10) && slideControls) {
        slideControls.classList.add("m:hidden");
      }
    }

    initSlider() {
      const { slideContainer } = queryDomNodes(this.selectors, this);
      if (!slideContainer) return;
      if (this.swiper && !this.swiper.destroyed) this.swiper.destroy(false, true);
      const controlsContainer = this.querySelector(".m-slider-controls");
      const prevButton = controlsContainer?.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer?.querySelector(".m-slider-controls__button-next");
      const slideItemsLength = slideContainer.querySelector(".swiper-wrapper")?.childElementCount || 0;
      slideContainer.classList.add("swiper-container");

      this.slider = new MinimogLibs.Swiper(slideContainer, {
        slidesPerView: 2,
        showPagination: this.showPagination,
        showNavigation: this.showNavigation,
        loop: this.products > parseInt(this.items, 10),
        pagination: this.showPagination
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
            }
          : false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.items, 10) >= 3 ? 3 : parseInt(this.items, 10),
          },
          1024: {
            slidesPerView: parseInt(this.items, 10) >= 4 ? 4 : parseInt(this.items, 10),
          },
          1280: {
            slidesPerView: parseInt(this.items, 10),
          },
        },
        threshold: 2,
        on: {
          init: () => {
            setTimeout(() => {
              const firstItem = this.querySelector(".m-image") || this.querySelector(".m-placeholder-svg");
              if (!firstItem || !controlsContainer) return;
              controlsContainer.style.setProperty("--offset-top", `${parseInt(firstItem.clientHeight, 10) / 2}px`);
              if (prevButton) prevButton.classList.remove("m:hidden");
              if (nextButton) nextButton.classList.remove("m:hidden");
            }, 200);
          },
          breakpoint: (swiper, breakpointParams) => {
            if (!controlsContainer) return;
            const { slidesPerView } = breakpointParams;
            if (slideItemsLength > slidesPerView) {
              controlsContainer.classList.remove("m:hidden");
              swiper.allowTouchMove = true;
            } else {
              controlsContainer.classList.add("m:hidden");
              swiper.allowTouchMove = false;
            }
          },
        },
      });

      if (this.slider && this.showNavigation) {
        if (prevButton) prevButton.onclick = () => this.slider.slidePrev();
        if (nextButton) nextButton.onclick = () => this.slider.slideNext();
      }

      this.swiper = slideContainer.swiper;
    }
  }

  customElements.define("m-recently-viewed", MRecentlyViewed);
}
